import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 * Handles positioning for both LTR and RTL layouts.
 */
export class FloatingPanelComponent extends HTMLElement {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const viewportWidth = window.innerWidth;
    const isRtl = document.documentElement.dir === 'rtl';

    // Get the parent details element
    const details = this.closest('details');
    if (!details) return;

    // Get panel dimensions - wait a frame to ensure accurate measurement
    await new Promise(resolve => requestAnimationFrame(resolve));

    const panelWidth = Math.max(this.scrollWidth, this.offsetWidth, 200);
    const detailsRect = details.getBoundingClientRect();

    // Calculate max width to prevent panel from being wider than viewport
    const maxPanelWidth = viewportWidth - (OFFSET * 2);
    this.style.setProperty('max-width', `${maxPanelWidth}px`, 'important');

    // Set top offset
    this.style.setProperty('top', `${OFFSET}px`, 'important');

    // Ensure high z-index to appear above accessibility widgets
    this.style.setProperty('z-index', '9999', 'important');

    // Calculate the safe zone for the panel
    const safeLeftEdge = OFFSET;
    const safeRightEdge = viewportWidth - OFFSET;
    const effectivePanelWidth = Math.min(panelWidth, maxPanelWidth);

    // CSS sets `left: 0` by default for both LTR and RTL
    // Panel anchors at parent's LEFT edge and extends RIGHT
    // JavaScript handles overflow by adjusting the left position

    let adjustment = 0;

    // Calculate panel position based on parent's left edge
    const panelLeftEdge = detailsRect.left;
    const panelRightEdge = detailsRect.left + effectivePanelWidth;

    // Check if panel overflows on the right
    if (panelRightEdge > safeRightEdge) {
      // Need to shift panel LEFT (negative adjustment)
      adjustment = safeRightEdge - panelRightEdge;
    }
    // Check if panel overflows on the left
    else if (panelLeftEdge < safeLeftEdge) {
      // Need to shift panel RIGHT (positive adjustment)
      adjustment = safeLeftEdge - panelLeftEdge;
    }

    // Apply positioning adjustment
    if (adjustment !== 0) {
      this.style.setProperty('left', `${adjustment}px`, 'important');
      this.style.setProperty('right', 'auto', 'important');
    } else {
      this.style.removeProperty('left');
      this.style.removeProperty('right');
    }
  };

  #detailsObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        const parent = this.closest('details');
        if (parent instanceof HTMLDetailsElement) {
          if (!parent.open) {
            // Details was closed, clear all positioning and constraints
            this.style.removeProperty('left');
            this.style.removeProperty('right');
            this.style.removeProperty('top');
            this.style.removeProperty('max-width');
            this.style.removeProperty('z-index');
          } else {
            // Details was opened, position IMMEDIATELY before animation starts
            // This prevents any flash of incorrectly positioned content
            this.#updatePosition();
          }
        }
      }
    }
  });

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);

    // Observe the parent details element for open/close state changes
    const parent = this.closest('details');
    if (parent instanceof HTMLDetailsElement) {
      this.#detailsObserver.observe(parent, { attributes: true, attributeFilter: ['open'] });

      // If details is already open on page load, position it
      if (parent.open) {
        requestIdleCallback(() => {
          this.#updatePosition();
        });
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#detailsObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
